<!-- @hmr:reset -->
<script lang="ts" context="module">
    import InfoActivity from "../Components/Activities/InfoActivity.svelte";

    const activityComponents = {
        info: InfoActivity
    };

</script>
<script lang="ts">
    import MapComponent from "../Components/Map.svelte";
    import RouteState from "../Components/RouteState.svelte";
    import TopBar from "../Components/TopBar.svelte";
    import RoutePath from "../Components/RoutePath.svelte";
    import Marker from "../Components/Marker.svelte";
    import { onDestroy, setContext } from "svelte";
    import type { CameraOptions, LngLat, MapTouchEvent, Point } from "mapbox-gl";
    import Slider from "../Components/Slider.svelte";
    import Slide from "../Components/Slide.svelte";
    import { get, readable, writable } from "svelte/store";
    import { fade } from "svelte/transition";
    import CheckpointActivites from "../Components/CheckpointActivites.svelte";
    import Modal from "../Components/Modal.svelte";
    import RoutePicturesSlider from "../Components/RoutePicturesSlider.svelte";
    import type { Activity, CheckpointDistance, Coordinates, Fn } from "../global";
    import { Race } from "../Model/Race";
    import type { Route } from "../Model/Route";
    import { RoutePicture } from "../Model/RoutePicture";
    import { appColor, challengeFactory, distanceCalculator, geolocation, picturesService, questionsService, raceService, routesService, user } from "../Services";
    import MapPopup from "../Components/MapPopup.svelte";
    import ChallengeModal from "../Components/ChallengeModal.svelte";
    import TooFarModal from "../Components/TooFarModal.svelte";
    import { navigate, useParams } from "svelte-navigator";
    import type Checkpoint from "../Model/Checkpoint";
    import type RouteParams from "svelte-navigator/types/RouteParam";
    import { DISTANCE_TO_INTERACTIVE, MAX_DISTANCE_TO_AUTOFOCUS, NUM_CHALLENGE_POINTS, NUM_QUESTIONS_BY_POINT, POINTS_CHALLENGE, POINTS_CHECKPOINT, POINTS_PICTURE } from "../environment";
    import type ChallengePoint from "../Model/ChallengePoint";
    import ChallengePointMarker from "../Components/ChallengePointMarker.svelte";
    import SendPictureBtn from "../Components/SendPictureBtn.svelte";
    import CheckpointMarker from "../Components/CheckpointMarker.svelte";
    import AuthModal from "../Components/AuthModal.svelte";
    import { delay } from "../Services/Helpers";
    import StartRaceBtn from "../Components/StartRaceBtn.svelte";

    const devMode = true;
    const avatarMarkerWidth = 60;
    const avatarMarkerHeight = avatarMarkerWidth;
    const showCheckpoint = writable<Checkpoint | false>(false);
    const mapSliderFxDuration = writable<number>(500);
    const showChallengePoint = writable<ChallengePoint | null>(null);
    const userLocation = geolocation.coords;
    const geolocationIsDenied = geolocation.isDenied;
    const params = useParams();
    const usePitch = 60;
    const followModeZoomLevel = 17;
    const routeW = writable<Route | null>(null);
    const routeR = readable<Route | null>(null, set => routeW.subscribe(set));
    const isExplorationModeW = writable(true);
    const isExplorationModeR = writable(true, set => isExplorationModeW.subscribe(set));

    let currentRouteId: string | null = null;
    let currentCheckpointId: string | null = null;
    let race: Race;
    let route: Route;
    let unsubscribeGeolocation: Fn | undefined;
    let mapWrapperElement: HTMLElement;
    let isExplorationMode: boolean = true;
    let isRaceMode: boolean = false;
    let activityComponent = null;
    let showActivity: Activity | null = null;
    let routePictures: RoutePicture[] = [];
    let showRoutePicture: RoutePicture | null = null;
    let innerCheckpoints: Checkpoint[];
    let showTooFarModal: boolean = false;
    let checkpointsActiveIndex: number;
    let prevPlayerLon: number;
    let invertPlayer: boolean = false;
    let closeToStartRace = false;
    let closestCheckpoint: Checkpoint;
    let showGetCloseToRouteStart = false;
    let checkpointsSliderPrev: Fn;
    let checkpointsSliderNext: Fn;
    let focusPlayerTimeout: number;
    let showAuthModal = false;
    let mapPopupWrapper: HTMLElement | null = null;
    let enableUserFollowUser = false;
    let enableUserFollowApp = false;
    let showPlayerMarker = false;
    let mapComponent: MapComponent;
    let mapIsInteractive = false;
    let userPlayerIsDragging = false;

    $: introAnimation(route, mapComponent);
    $: updateStartRaceBtnStatus($userLocation, route);
    $: if ($showCheckpoint) {
        mapPopupPointCoords($showCheckpoint.coordinates, mapWrapperElement, mapPopupWrapper);
    }

    setContext('openActivity', (checkpoint: Checkpoint, activity: Activity) => {
        if (isRaceMode && !$userLocation) {
            return;
        }

        if (activity.restrict.includes('distance')) {

            const distance = distanceCalculator.calcDistance(checkpoint.coordinates, $userLocation);

            if (distance > (checkpoint.interactiveDistance || DISTANCE_TO_INTERACTIVE)) {
                showTooFarModal = true;
                return;
            }
        }

        if (isRaceMode && $showCheckpoint) {

            if (!race.hasCheckpointInTrack(checkpoint.id)) {
                race.addPoints(POINTS_CHECKPOINT);
            }

            race.addRaceCheckpoint($showCheckpoint.id);
            raceService.saveRace(race);
        }

        showActivity = activity;
        activityComponent = activityComponents[showActivity.type];
    });

    setContext('addPicture', (picture: RoutePicture) => {
        routePictures = [...routePictures, picture];

        if (isRaceMode) {
            race.addPoints(POINTS_PICTURE);
        }
    });

    setContext('startRace', onStartRace);
    setContext('route', routeR);
    setContext('isExplorationMode', isExplorationModeR);

    userLocation.subscribe(coords => {
        if (!coords) {
            return;
        }
        if (prevPlayerLon !== undefined) {
            invertPlayer = coords.lon < prevPlayerLon;
        }
        prevPlayerLon = coords.lon;
        centerMapOnUser();
    });

    onDestroy(() => {
        unsubscribeGeolocationService();
    });

    showCheckpoint.subscribe(checkpoint => {
        if (!route || checkpoint) {
            return;
        }
        navigate(`/mapa/${route.id}`);
    });

    params.subscribe(onParamsChange);

    async function centerMapOnUser() {
        if (!enableUserFollowUser || !enableUserFollowApp || !$userLocation || !mapComponent) {
            return;
        }
        await mapComponent.panTo($userLocation);
    }

    function updateStartRaceBtnStatus(userLocation: Coordinates, route: Route) {
        if (!userLocation || !route) {
            return;
        }

        const checkpointDistances: Array<CheckpointDistance> = [route.firstCheckpoint, route.lastCheckpoint].map(checkpoint => {
            return {
                checkpoint,
                distance: distanceCalculator.calcDistance(checkpoint.coordinates, userLocation),
            };
        });

        checkpointDistances.sort(({ distance: d1 }, { distance: d2 }) => d1 - d2);

        const [closestCheckpointDistance] = checkpointDistances;
        
        closeToStartRace = closestCheckpointDistance.distance < DISTANCE_TO_INTERACTIVE;
        closestCheckpoint = closestCheckpointDistance.checkpoint;
    }

    async function onParamsChange(routeParams: RouteParams) {
        const { params } = routeParams;

        if (!params) {
            navigate('/menu');
        }

        const [routeId, checkpointId] = params.split('/');

        if (!routeId) {
            navigate('/menu');
        }

        await loadRoute(routeId);

        openCheckpoint(checkpointId);

    }

    async function loadRoute(routeId: string): Promise<void> {
        if (currentRouteId === routeId) {
            return;
        }
        
        currentRouteId = routeId;
        route = await routesService.getRoute(routeId);

        if (!route) {
            //Sin una ruta no es posible continuar
            navigate("/menu");
            return;
        }

        routeW.set(route);
        appColor.set(route.color);

        //Colocamos los checkpoints de la ruta en las variables adecuadas
        innerCheckpoints = route.checkpoints.slice(1, -1);

        getPictures();
        
        if (!(await raceService.hasRace())) {
            return;
        }

        const currentRace = await raceService.loadRace();

        if (!get(currentRace.isEnded) && currentRace.routeId === route.id) {
            setRace(currentRace);
        }
    }

    async function introAnimation(route: Route, mapComponent: MapComponent) {
        if (!route || !mapComponent) {
            return;
        }
        
        const bounds = route.getBounds();
        const options: CameraOptions = {
            padding: 50,
            pitch: usePitch,
        };
        
        await mapComponent.mapLookAtBounds({ bounds, options });
        await delay(1000);

        if (await focusPlayer(mapComponent, $userLocation)) {
            enableFollowMode();
        } else {
            enableFreeMode();
        }

        showPlayerMarker = true;
        mapIsInteractive = true;
    }

    async function focusPlayer(mapComponent: MapComponent, userLocation: Coordinates): Promise<boolean> {
        const distaceToStart = distanceCalculator.calcDistance(route.firstCheckpoint.coordinates, userLocation);
        const distaceToEnd = distanceCalculator.calcDistance(route.lastCheckpoint.coordinates, userLocation);
        
        if (distaceToStart > MAX_DISTANCE_TO_AUTOFOCUS && distaceToEnd > MAX_DISTANCE_TO_AUTOFOCUS) {
            return false;
        }

        enableUserFollowApp = true;
        enableUserFollowUser = true;

        await mapComponent.flyTo({
            center: userLocation,
            zoom: followModeZoomLevel,
            duration: 1500,
            bearing: 30,
        });

        return true;
    }

    function toggleGameMode() {
        if (enableUserFollowUser) {
            enableFreeMode();
        } else {
            enableFollowMode();
        }
    }

    function enableFreeMode() {
        enableUserFollowApp = false;
        enableUserFollowUser = false;
        
        mapComponent.map.scrollZoom.enable();
        mapComponent.map.dragPan.enable();
        mapComponent.map.touchPitch.enable();
        mapComponent.map.touchZoomRotate.enable();
        mapComponent.flyTo({
            zoom: 15,
            pitch: 0,
            bearing: 0,
            duration: 1000
        });
    }

    function enableFollowMode() {
        if (!$userLocation) {
            return;
        }
        if (enableUserFollowUser) {
            centerMapOnUser();
        } else {
            mapComponent.map.scrollZoom.disable();
            mapComponent.map.dragPan.disable();
            mapComponent.map.touchPitch.disable();
            mapComponent.map.touchZoomRotate.disable();
            mapComponent.flyTo({
                center: $userLocation,
                pitch: usePitch,
                bearing: 30,
                duration: 1000,
                zoom: followModeZoomLevel
            });
        }
        enableUserFollowApp = true;
        enableUserFollowUser = true;
    }

    async function createRace(initCheckpoint: Checkpoint) {
        if (!route) {
            throw new Error('No es posible crear una carrera si no hay una ruta.');
        }

        const questions = await questionsService.getQuestions();
        const challengePoints = challengeFactory.createChallenge(route, questions, NUM_CHALLENGE_POINTS, NUM_QUESTIONS_BY_POINT);
        
        setRace(new Race(route.id, initCheckpoint.id, new Date, challengePoints));
    }

    async function setRace(raceToSet: Race) {

        const hours = 60 * 60 * 2 * 1000; // 2 horas
        
        if ((Date.now() - raceToSet.startTime.getTime()) > hours) {
            //la carrera ha expirado porque hace más de 2 horas que fue iniciada
            raceService.clearRace();
            return;
        }

        race = raceToSet;
        await raceService.saveRace(race);
        isExplorationMode = false;
        isRaceMode = true;
        isExplorationModeW.set(false);
    }

    async function getPictures() {
        const pictures = await picturesService.findByLabels(['rutas-saludables-pto-cruz']);
        routePictures = pictures.map(picture => new RoutePicture(picture));
    }

    function userMarkerTouchstart() {
        userPlayerIsDragging = true;
    }

    function userMarkerTouchend() {
        userPlayerIsDragging = false;
    }

    function userMarkerDragend({ detail: position }: CustomEvent<LngLat>) {
        geolocation.override({
            lon: position.lng,
            lat: position.lat,
        });
    }

    function unsubscribeGeolocationService() {
        if (!unsubscribeGeolocation) {
            return;
        }
        unsubscribeGeolocation();
        unsubscribeGeolocation = null;
    }

    function openCheckpoint(checkpointId: string): void {
        if (!checkpointId || currentCheckpointId === checkpointId || !route) {
            return;
        }

        currentCheckpointId = checkpointId;
        const checkpoint = route.checkpoints.find(({ id }) => id === checkpointId);

        if (!checkpoint) {
            return;
        }

        mapSliderFxDuration.set(500);
        $showCheckpoint = checkpoint;
        $showChallengePoint = null;
        enableUserFollowApp = false;
        cancelFocusPlayer();
    }

    function onStartRace() {

        if (!$userLocation || !route || isRaceMode || !closestCheckpoint) {
            return;
        }

        if (!closeToStartRace) {
            //El usuario no está suficientemente cerca del primer checkpoint
            //como para comenzar una carrera.
            showGetCloseToRouteStart = true;
            return;
        }

        createRace(closestCheckpoint);
        closeAllActivities();
    }

    function onCheckpointClick(checkpoint: Checkpoint) {
        if ($showCheckpoint === checkpoint) {
            return;
        }

        if (!isEndCheckpoint(checkpoint)) {
            navigate(`/mapa/${route.id}/${checkpoint.id}`);
            return;
        }

        if (!canEndRaceInCheckpoint(checkpoint)) {
            showTooFarModal = true;
            return;
        }

        if (!$user) {
            showAuthModal = true;
            return;
        }

        //La carrera ha sido terminada
        endRace();
        
    }

    async function endRace() {
        if (!isRaceMode) {
            return;
        }
        race.setEnded();
        await raceService.saveRace(race);
        navigate('/compartir');
    }

    function isEndCheckpoint(checkpoint: Checkpoint): boolean {
        if (!isRaceMode || !route) {
            return false;
        }

        if (checkpoint.id !== route.firstCheckpoint.id && checkpoint.id !== route.lastCheckpoint.id) {
            return false;
        }

        if (checkpoint.id === race.startCheckpointId) {
            return false;
        }

        return true;
    }

    /**
     * Determina si la carrera actual puede ser terminada en el checkpoint indicado
     */
    function canEndRaceInCheckpoint(checkpoint: Checkpoint): boolean {

        if (!$userLocation) {
            return false;
        }

        const distance = distanceCalculator.calcDistance(checkpoint.coordinates, $userLocation);
        if (distance > DISTANCE_TO_INTERACTIVE) {
            return false;
        }

        return true;
    }

    function onMapSliderChange({ detail: checkpoint }: CustomEvent<Checkpoint>) {
        navigate(`/mapa/${route.id}/${checkpoint.id}`);
    }

    function onChallengePointClick(challengePoint: ChallengePoint): void {
        if (!$userLocation) {
            return;
        }

        const distance = distanceCalculator.calcDistance(challengePoint.coords, $userLocation);

        if (distance > DISTANCE_TO_INTERACTIVE) {
            showTooFarModal = true;
            return;
        }

        $showChallengePoint = challengePoint;
    }

    function mapPopupPointCoords(coords: Coordinates, mapWrapperElement: HTMLElement, mapPopupWrapper: HTMLElement): void {
        if (!coords || !mapWrapperElement || !mapPopupWrapper) {
            return;
        }

        const mapPopupWrapperHeight = mapPopupWrapper.offsetHeight;
        const mapWrapperElementHeight = mapWrapperElement.offsetHeight;
        const offsetY = mapPopupWrapperHeight - (mapWrapperElementHeight / 2);

        mapComponent.panTo(coords, {
            offset: [0, offsetY]
        });
    }

    function onMapPopupClose() {
        closeAllActivities();
    }

    function onDragstart() {
        closeAllActivities();
    }

    function cancelFocusPlayer() {
        window.clearTimeout(focusPlayerTimeout);
    }

    function closeAllActivities() {
        mapSliderFxDuration.set(0);
        $showCheckpoint = false;
        currentCheckpointId = null;
        enableUserFollowApp = true;
        activityComponent = null;
        centerMapOnUser();
        navigate(`/mapa/${route.id}`);
    }

    function onPictureClick(routePicture: RoutePicture) {
        showRoutePicture = routePicture;
        cancelFocusPlayer();
    }

    function closeRoutePictureModal() {
        showRoutePicture = null;
    }

    function onBack() {
        navigate('/menu');
    }

    async function onGeolocate() {
        const userLocation = await geolocation.getPosition(true);
        await mapComponent.flyTo({
            center: userLocation,
            zoom: 18,
            duration: 1500,
        });
    }

    function onChallengeSuccess() {
        if (!isRaceMode || !$showChallengePoint) {
            return;
        }

        if (!race.hasCheckpointInTrack($showChallengePoint.id)) {
            race.addPoints(POINTS_CHALLENGE);
        }

        $showChallengePoint.setCompleted();
        race.addRaceCheckpoint($showChallengePoint.id);
        raceService.saveRace(race);
        $showChallengePoint = null;
    }

    function onChallengeFail() {
        if (!isRaceMode || !$showChallengePoint) {
            return;
        }
        $showChallengePoint = null;
    }

    let capureAngle: number;
    let initBearing: number;

    function onMapTouchstart({ detail: event }: CustomEvent<MapTouchEvent>) {
        if (!enableUserFollowUser || userPlayerIsDragging) {
            return;
        }
        event.originalEvent.preventDefault();
        capureAngle = calcAngleBetweenPoints(mapComponent.map.project($userLocation), event.point);
        initBearing = mapComponent.map.getBearing();
    }

    function onMapTouchmove({ detail: event }: CustomEvent<MapTouchEvent>) {
        if (!enableUserFollowUser || userPlayerIsDragging) {
            return;
        }
        event.originalEvent.preventDefault();
        const angle = calcAngleBetweenPoints(mapComponent.map.project($userLocation), event.point);
        const diff = angle - capureAngle;
        mapComponent.map.setBearing(initBearing + diff);
    }

    function calcAngleBetweenPoints(point1: Point, point2: Point): number {
        const PIx2 = Math.PI * 2;
        const diff = point2.sub(point1);
        let angle = Math.atan2(diff.y, diff.x);
        if (angle < 0) {
            angle = PIx2 + angle;
        }
        return 360 - (360 * (angle / PIx2));
    }

</script>

<style lang="scss">

    @use '../variables';

    .mapa {
        height: var(--vh, 100vh);
        display: flex;
        flex-direction: column;
    }

    .map-wrapper {
        flex: 1 1 0;
        position: relative;
        overflow: hidden;
        touch-action: none;
        pointer-events: none;

        &.interactive {
            touch-action: auto;
            pointer-events: auto;
        }
    }

    .map-popup-wrapper {
        position: absolute;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
        top: 0;
        left: 0;
        z-index: 500;
        padding: 2rem 2rem 0;
        box-sizing: border-box;
    }

    .slider-wrapper {
        flex: 1 1 0;
        width: 100%;
        border-radius: 1rem;
        position: relative;

        .arrow {
            $width: 1.5rem;
            $height: 2rem;
            position: absolute;
            top: calc(50% - #{$height * 0.5});
            width: $width;
            height: $height;
            object-fit: contain;
            filter: drop-shadow(0 2px 4px #888);
        }

        .arrow-left {
            right: calc(100% + 0.25rem);
        }
        
        .arrow-right {
            left: calc(100% + 0.25rem);
            transform: scaleX(-1);
        }
    }

    .checkpoint-slide {
        height: 100%;
        padding: 1rem;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;

        .image {
            position: relative;

            &::before {
                content: '';
                display: block;
                padding-top: 56.25%;
            }

            img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        .meta {
            flex: 1 1 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        .name {
            font-size: 20px;
            color: var(--color-secondary);
            text-align: center;
        }

        .desc {
            font-size: 15px;
            color: var(--color-highlight);
            text-align: center;
        }

    }

    .route-pictures {
        height: calc(100% - 3rem);
    }

    .marker-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .invert {
        transform: scaleX(-1);
    }

    .geolocation-is-denied {
        background-color: #FFEB00;
        color: var(--color-secondary);
        padding: 0.5rem 1rem;
        font-size: var(--instruction-size);
        display: flex;
        align-items: center;
        gap: 0.6rem;
    }

    .bottom-bar {
        padding: 1rem;
        background-color: var(--route-color, --color-primary);
        display: flex;
        gap: 1rem;
    }

    .left-area, .right-area {
        flex: 1 1 0;
        display: flex;
        height: 3rem;
    }

    .right-area {
        justify-content: flex-end;
    }

    .gamemode-btn {
        $size: 3rem;
        width: $size;
        height: $size;
        white-space: nowrap;
        overflow: hidden;
        text-indent: 1000px;
        background-image: url('/images/geolocate-btn.svg');
        background-color: transparent;
        background-size: contain;
        border: none;

        &.free-mode {
            filter: grayscale(1);
            opacity: 0.5;
        }
    }

    @media (min-height: variables.$big-phone-height) {

        .map-popup-wrapper {
            min-height: 50%;
        }

        .checkpoint-slide {

            .name {
                font-size: 30px;
            }

        }

    }

</style>

<div class="mapa view-container" transition:fade>
    <TopBar title={route?.name || 'Mapa'} on:back={onBack}>
        <div slot="btn">
            {#if route}
            <SendPictureBtn {route} />
            {/if}
        </div>
    </TopBar>

    {#if isRaceMode}
        <RouteState {race} />
    {/if}

    {#if $geolocationIsDenied}
    <div class="geolocation-is-denied">
        <a href="https://routingtales.com/como-activo-mi-gps/" target="_blank"><img src="/images/geolocation-fail.svg" alt=""> Active el GPS para acceder a todos los contenidos.</a>
    </div>
    {/if}

    <div class="map-wrapper" class:interactive={mapIsInteractive} bind:this={mapWrapperElement}>
        <MapComponent
            bind:this={mapComponent}
            on:dragstart={onDragstart}
            on:touchstart={onMapTouchstart}
            on:touchmove={onMapTouchmove}>
            {#if route}
                <RoutePath path={route.path} color={route.color} width={6} />
                <CheckpointMarker checkpoint={route.firstCheckpoint} on:click={() => onCheckpointClick(route.firstCheckpoint)} />
                <CheckpointMarker checkpoint={route.lastCheckpoint} on:click={() => onCheckpointClick(route.lastCheckpoint)} />
                {#each innerCheckpoints as checkpoint}
                    <CheckpointMarker {checkpoint} on:click={() => onCheckpointClick(checkpoint)} />
                {/each}

                {#if showPlayerMarker && $userLocation}
                    <Marker
                        coordinates={$userLocation}
                        draggable={devMode}
                        width={avatarMarkerWidth}
                        height={avatarMarkerHeight}
                        zIndex={15}
                        on:touchstart={userMarkerTouchstart}
                        on:touchend={userMarkerTouchend}
                        on:dragend={userMarkerDragend}>
                        <lottie-player
                            class:invert={invertPlayer}
                            src="/lotties/mascota_ajuste.json"
                            background="transparent"
                            speed="1"
                            loop autoplay></lottie-player>
                    </Marker>
                {/if}

                {#if isRaceMode}
                    {#each race.challengePoints as challengePoint}
                        <ChallengePointMarker
                            {route}
                            {challengePoint}
                            on:click={() => onChallengePointClick(challengePoint)} />
                    {/each}
                {/if}

                {#each routePictures as routePicture}
                    <Marker
                        coordinates={routePicture}
                        zIndex={2}
                        anchor="center"
                        on:click={() => onPictureClick(routePicture)}>
                        <img class="marker-image" src="/images/icon-picture.svg" alt="">
                    </Marker>
                {/each}
            {/if}
        </MapComponent>

        {#if $showCheckpoint}
        <div class="map-popup-wrapper disable-interactive" transition:fade bind:this={mapPopupWrapper}>
            <MapPopup on:close={onMapPopupClose}>
                {#if $showCheckpoint}
                <div class="slider-wrapper">
                    <img class="arrow arrow-left" src="/images/angle-white.svg" alt="" on:click={checkpointsSliderPrev}>
                    <img class="arrow arrow-right" src="/images/angle-white.svg" alt="" on:click={checkpointsSliderNext}>
                    <Slider
                            --height="100%"
                            slideTo={$showCheckpoint}
                            on:change={onMapSliderChange}
                            bind:prev={checkpointsSliderPrev}
                            bind:next={checkpointsSliderNext}
                            preloadImages={false}
                            lazy={{ loadPrevNext: true }}
                            bind:activeIndex={checkpointsActiveIndex}>
                        {#each route.checkpoints as checkpoint, index}
                        <Slide index={checkpoint}>
                            <div class="checkpoint-slide">
                                <div class="image">
                                    <img data-src={checkpoint.image} alt={checkpoint.name} class="swiper-lazy">
                                    <div class="swiper-lazy-preloader"></div>
                                </div>
                                <div class="meta">
                                    <div class="name">{checkpoint.name}</div>
                                    <div class="desc">{checkpoint.desc}</div>
                                    {#if checkpointsActiveIndex === index}
                                    <div transition:fade>
                                        <CheckpointActivites {checkpoint} />
                                    </div>
                                    {/if}
                                </div>
                            </div>
                        </Slide>
                        {/each}
                    </Slider>
                </div>
                {/if}
            </MapPopup>
        </div>
        {/if}

    </div>

    <div class="bottom-bar">

        <div class="left-area">
            <button
                type="button"
                class="gamemode-btn"
                class:free-mode={!enableUserFollowUser}
                class:follow-mode={enableUserFollowUser}
                on:click={toggleGameMode}>Geolocalizar</button>
        </div>
    
        <div class="right-area">
            {#if isExplorationMode}
            <StartRaceBtn />
            {/if}
        </div>
    
    </div>
</div>

{#if activityComponent}
<Modal --zIndex="600">
    <svelte:component
        this={activityComponent}
        activity={showActivity}
        on:back={() => activityComponent = null} />
</Modal>
{/if}

{#if showRoutePicture}
<Modal>
    <TopBar on:back={closeRoutePictureModal} title="Fotos usuarios" />
    <div class="route-pictures">
        <RoutePicturesSlider --height="100%" {routePictures} showPicture={showRoutePicture} />
    </div>
</Modal>
{/if}

{#if $showChallengePoint}
<ChallengeModal
    --zIndex="1000"
    challenge={$showChallengePoint}
    on:close={() => $showChallengePoint = null}
    on:success={onChallengeSuccess}
    on:fail={onChallengeFail} />
{/if}

<TooFarModal
    --zIndex="500"
    show={showTooFarModal}
    distance={DISTANCE_TO_INTERACTIVE}
    on:close={() => showTooFarModal = false} />

<TooFarModal
    --zIndex="500"
    show={showGetCloseToRouteStart}
    on:close={() => showGetCloseToRouteStart = false}>
    Debes estár al menos a {DISTANCE_TO_INTERACTIVE}m del punto de inicio para empezar la ruta.
</TooFarModal>

<AuthModal open={showAuthModal} on:close={() => showAuthModal = false} on:auth_success={endRace}>
    <div slot="instructions">
        <h4>¡Has llegado al final de la carrera!</h4>
        <p>Inicia sesión para compartir tu resultado en las redes sociales.</p>
    </div>
</AuthModal>

